import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Camera } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import Button from "@/components/Button";
import { Field } from "@/components/Form";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fileToDataUrl } from "@/lib/vehicles";

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [photo, setPhoto] = useState(user?.photo || null);
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef(null);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(await fileToDataUrl(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (phone !== user?.phone) body.phone = phone.trim();
      if (photo && photo !== user?.photo) body.photo = photo;
      await api("/me", { method: "PATCH", body });
      await refreshUser();
      toast.success("Profile updated");
      navigate(-1);
    } catch (e) {
      toast.error(e?.message || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
        <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-foreground">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-bold text-foreground">Edit profile</h1>
      </header>

      <div className="mx-auto max-w-md px-5 py-8">
        <div className="mb-6 flex flex-col items-center gap-3">
          <button onClick={() => inputRef.current?.click()} className="relative" data-testid="change-photo">
            <Avatar src={photo} name={user?.name} size={96} />
            <span className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white ring-4 ring-background">
              <Camera className="h-4 w-4" />
            </span>
          </button>
          <input ref={inputRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" />
          <p className="text-sm text-muted-foreground">Tap to change photo</p>
        </div>

        <div className="space-y-4">
          <Field label="Name" value={user?.name || ""} disabled inputClassName="opacity-60" />
          <Field label="Email" value={user?.email || ""} disabled inputClassName="opacity-60" />
          <Field label="Phone number" data-testid="edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(407) 555-0123" />
        </div>

        <Button className="mt-6 w-full" onClick={save} loading={saving} data-testid="save-profile">Save changes</Button>
      </div>
    </div>
  );
}
